import env from "react-dotenv";
import { getCachedUserToken } from "./userToken";

function isLocalDevHost() {
  try {
    const host = window?.location?.hostname;
    return host === "localhost" || host === "127.0.0.1";
  } catch (_) {
    return false;
  }
}

function readEnvValue(key) {
  try {
    if (env && env[key]) return env[key];
  } catch (_) {
    /* ignore react-dotenv lookup failures */
  }

  try {
    if (typeof window !== "undefined" && window?.env && window.env[key]) {
      return window.env[key];
    }
  } catch (_) {
    /* ignore runtime env lookup failures */
  }

  return "";
}

function trimTrailingSlash(value) {
  return String(value || "").replace(/\/+$/, "");
}

function shouldUseSameOriginApi(path = "") {
  const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;
  if (!normalizedPath.startsWith("/api/")) return false;

  const directMode = String(readEnvValue("REACT_APP_DIRECT_SERVICENOW") || "").toLowerCase();
  return directMode !== "true";
}

export function getServiceNowBaseUrl() {
  return trimTrailingSlash(readEnvValue("REACT_APP_SERVICENOW_BASE_URL"));
}

export function buildServiceNowUrl(path) {
  const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;

  // Prefer same-origin /api calls in every environment. In local dev these go
  // through the CRA proxy; in Azure Static Web Apps they go through the SWA API.
  if (shouldUseSameOriginApi(normalizedPath)) {
    return normalizedPath;
  }

  const baseUrl = getServiceNowBaseUrl();

  if (!baseUrl) {
    throw new Error("REACT_APP_SERVICENOW_BASE_URL is not configured");
  }

  return `${baseUrl}${normalizedPath}`;
}

export function getServiceNowBasicAuthHeader() {
  const user = readEnvValue("REACT_APP_USER");
  const password = readEnvValue("REACT_APP_PASSWORD");

  if (!user || !password) return null;
  return `Basic ${btoa(`${user}:${password}`)}`;
}

export function getServiceNowHeaders({
  includeBasicAuth = false,
  contentType = null,
  extraHeaders = {},
} = {}) {
  const headers = {
    Accept: "application/json",
    ...extraHeaders,
  };

  if (contentType) {
    headers["Content-Type"] = contentType;
  }

  const userToken = getCachedUserToken({ includeUrl: true });
  if (userToken) {
    headers["X-userToken"] = userToken;
    headers["X-UserToken"] = userToken;
  }

  if (includeBasicAuth) {
    // In local dev, the CRA proxy injects credentials if configured.
    if (isLocalDevHost()) {
      return headers;
    }

    const authorization = getServiceNowBasicAuthHeader();
    // For deployed same-origin /api calls, allow the SWA API proxy to reuse the
    // demo credentials shipped to the client when no server-side creds exist.
    // Azure Static Web Apps may consume the standard Authorization header before
    // the Function sees it, so forward demo auth via a custom header instead.
    if (shouldUseSameOriginApi("/api/")) {
      if (authorization) {
        headers["X-ServiceNow-Authorization"] = authorization;
      }
      return headers;
    }

    if (!authorization) {
      throw new Error(
        "Temporary demo ServiceNow credentials are missing from REACT_APP_USER/REACT_APP_PASSWORD"
      );
    }
    headers.Authorization = authorization;
  }

  return headers;
}
